import type { SceneVisualState } from "../types";

export const phenologyStages = [
  {
    id: "late-winter",
    label: "观察时点一",
    peach: "桃树枝条尚未萌发",
    bird: "尚未观察到燕子归来",
    insect: "查看本时点的昆虫活动",
    // The instrument is visible before the learner writes an observation.
    // Keep the reading label neutral; the UI supplies the recorded/pending state.
    temperature: "本时点温度计读数",
    humidity: "本时点湿度计读数"
  },
  {
    id: "early-spring",
    label: "观察时点二",
    peach: "桃树嫩芽开始萌发",
    bird: "尚未观察到燕子归来",
    insect: "查看本时点的昆虫活动",
    temperature: "本时点温度计读数",
    humidity: "本时点湿度计读数"
  },
  {
    id: "mid-spring",
    label: "观察时点三",
    peach: "桃花开放",
    bird: "观察到燕子归来",
    insect: "查看本时点的昆虫活动",
    temperature: "本时点温度计读数",
    humidity: "本时点湿度计读数"
  },
  {
    id: "late-spring",
    label: "观察时点四",
    peach: "桃树叶片展开",
    bird: "查看本时点的燕子活动",
    insect: "查看本时点的昆虫活动",
    temperature: "本时点温度计读数",
    humidity: "本时点湿度计读数"
  }
] as const;

export const observationKinds = ["peach", "bird", "insect", "weather"] as const;
export type ObservationKind = (typeof observationKinds)[number];
export const recordableObservationKinds = ["peach"] as const satisfies readonly ObservationKind[];

export const phenologyTimelineAnchors = [0, 33, 67, 100] as const;

export const seasonTimelineAnchors = [0, 33, 67, 100] as const;
export const seasonTimelineIds = ["spring", "summer", "autumn", "winter"] as const;

export interface SeasonTimelineBlend {
  fromIndex: number;
  toIndex: number;
  mix: number;
  nearestIndex: number;
  weights: [number, number, number, number];
}

export function clampSeasonTimelinePosition(value: number): number {
  if (!Number.isFinite(value)) return seasonTimelineAnchors[0];
  return Math.min(seasonTimelineAnchors.at(-1)!, Math.max(seasonTimelineAnchors[0], value));
}

/** Piecewise-linear weights keep only two neighbouring season keyframes visible. */
export function getSeasonTimelineBlend(position: number): SeasonTimelineBlend {
  const value = clampSeasonTimelinePosition(position);
  const lastIndex = seasonTimelineAnchors.length - 1;
  let fromIndex = lastIndex;

  for (let index = 0; index < lastIndex; index += 1) {
    if (value <= seasonTimelineAnchors[index + 1]!) {
      fromIndex = index;
      break;
    }
  }

  const toIndex = Math.min(lastIndex, fromIndex + 1);
  const from = seasonTimelineAnchors[fromIndex]!;
  const to = seasonTimelineAnchors[toIndex]!;
  const mix = from === to ? 0 : (value - from) / (to - from);
  const weights: SeasonTimelineBlend["weights"] = [0, 0, 0, 0];
  weights[fromIndex] = 1 - mix;
  weights[toIndex] += mix;

  return {
    fromIndex,
    toIndex,
    mix,
    nearestIndex: mix >= 0.5 ? toIndex : fromIndex,
    weights
  };
}

/** Records every semantic season marker crossed, even when a drag reverses before release. */
export function withCrossedSeasons(
  items: readonly string[],
  previousPosition: number,
  nextPosition: number
): string[] {
  const lower = Math.min(
    clampSeasonTimelinePosition(previousPosition),
    clampSeasonTimelinePosition(nextPosition)
  );
  const upper = Math.max(
    clampSeasonTimelinePosition(previousPosition),
    clampSeasonTimelinePosition(nextPosition)
  );

  return seasonTimelineAnchors.reduce<string[]>((result, anchor, index) => {
    const seasonId = seasonTimelineIds[index]!;
    return anchor >= lower && anchor <= upper && !result.includes(seasonId)
      ? [...result, seasonId]
      : result;
  }, [...items]);
}

export function clampTimelinePosition(value: number): number {
  if (!Number.isFinite(value)) return phenologyTimelineAnchors[0];
  return Math.min(phenologyTimelineAnchors.at(-1)!, Math.max(phenologyTimelineAnchors[0], value));
}

export function getNearestPhenologyStage(position: number): number {
  const value = clampTimelinePosition(position);
  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;

  phenologyTimelineAnchors.forEach((anchor, index) => {
    const distance = Math.abs(value - anchor);
    // At an exact midpoint, advance to the later observation so keyboard and
    // forward dragging feel consistent.
    if (distance <= nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });

  return nearestIndex;
}

export function withCrossedPhenologyStages(
  items: readonly string[],
  previousPosition: number,
  nextPosition: number
): string[] {
  const lower = Math.min(clampTimelinePosition(previousPosition), clampTimelinePosition(nextPosition));
  const upper = Math.max(clampTimelinePosition(previousPosition), clampTimelinePosition(nextPosition));
  return phenologyTimelineAnchors.reduce<string[]>((result, anchor, index) => {
    const id = `stage-${index}`;
    return anchor >= lower && anchor <= upper && !result.includes(id) ? [...result, id] : result;
  }, [...items]);
}

export function getPhenologyObservationProgress(items: readonly string[]): {
  timepoints: number;
  totalTimepoints: number;
  objects: number;
  totalObjects: number;
  complete: boolean;
} {
  const timepoints = phenologyTimelineAnchors.filter((_, index) => items.includes(`stage-${index}`)).length;
  const objects = recordableObservationKinds.filter((kind) => items.includes(`observe:${kind}`)).length;
  return {
    timepoints,
    totalTimepoints: phenologyTimelineAnchors.length,
    objects,
    totalObjects: recordableObservationKinds.length,
    complete: timepoints === phenologyTimelineAnchors.length && objects === recordableObservationKinds.length
  };
}

export function withObservedStage(items: readonly string[], stage: number): string[] {
  const id = `stage-${stage}`;
  return items.includes(id) ? [...items] : [...items, id];
}

export function withObservedKind(items: readonly string[], kind: ObservationKind): string[] {
  const id = `observe:${kind}`;
  return items.includes(id) ? [...items] : [...items, id];
}

export function isPhenologyObservationComplete(items: readonly string[]): boolean {
  return getPhenologyObservationProgress(items).complete;
}

export type FactorId = "latitude" | "longitude" | "elevation" | "era";

export interface SemanticOption {
  value: string;
  label: string;
  shortLabel: string;
  normalized: number;
}

export interface SemanticControlGroup {
  id: string;
  label: string;
  options: readonly SemanticOption[];
}

export interface FactorSemanticDefinition {
  id: FactorId;
  groups: readonly SemanticControlGroup[];
  claim: string;
  boundary: string;
}

export const factorSemantics: Record<FactorId, FactorSemanticDefinition> = {
  latitude: {
    id: "latitude",
    groups: [
      {
        id: "latitudeCity",
        label: "比较城市",
        options: [
          { value: "nanjing", label: "南京", shortLabel: "南京", normalized: 0.15 },
          { value: "beijing", label: "北京", shortLabel: "北京", normalized: 0.85 }
        ]
      },
      {
        id: "latitudeSeason",
        label: "春季阶段",
        options: [
          { value: "early-spring", label: "早春 · 桃花", shortLabel: "早春", normalized: 0.2 },
          { value: "late-spring", label: "晚春 · 刺槐", shortLabel: "晚春", normalized: 0.75 }
        ]
      }
    ],
    claim: "通常越往北，春季物候越迟。",
    boundary: "南京桃花约早 20 天；到晚春刺槐约早 10 天，差值不是固定常数。"
  },
  longitude: {
    id: "longitude",
    groups: [{
      id: "longitudePlace",
      label: "海陆位置",
      options: [
        { value: "beijing-inland", label: "北京 · 内陆", shortLabel: "内陆北京", normalized: 0.18 },
        { value: "dalian-coast", label: "大连 · 沿海", shortLabel: "沿海大连", normalized: 0.82 }
      ]
    }],
    claim: "同纬度附近，沿海地区的春季物候通常比内陆迟。",
    boundary: "课文中，城市对比约迟一周，不能推广为所有沿海城市都精确迟七天。"
  },
  elevation: {
    id: "elevation",
    groups: [
      {
        id: "elevationLevel",
        label: "山地位置",
        options: [
          { value: "foot", label: "山脚", shortLabel: "山脚", normalized: 0.08 },
          { value: "middle", label: "山腰", shortLabel: "山腰", normalized: 0.5 },
          { value: "top", label: "山顶", shortLabel: "山顶", normalized: 0.92 }
        ]
      },
      {
        id: "elevationSeason",
        label: "季节模式",
        options: [
          { value: "spring-summer", label: "春夏 · 抽青开花", shortLabel: "春夏", normalized: 0.25 },
          { value: "autumn", label: "秋季 · 落叶", shortLabel: "秋季", normalized: 0.78 }
        ]
      }
    ],
    claim: "春夏高处抽青、开花较迟，秋季高处落叶较早。",
    boundary: "这是一般规律；逆温时可出现山脚有霜、山腰无霜的例外。"
  },
  era: {
    id: "era",
    groups: [{
      id: "eraPeriod",
      label: "每年同一时段，春季物候是否同时到来？",
      options: [
        { value: "1741-1750", label: "1741-1750年春季物候到来平均时间", shortLabel: "十八世纪", normalized: 0.12 },
        { value: "1921-1930", label: "查看1921-1930年的同一时间情况", shortLabel: "二十世纪", normalized: 0.88 }
      ]
    }],
    claim: "较近代十年平均的春季物候更早。",
    boundary: "两组十年平均相差约九天，不表示每一年、每棵树都提前九天。"
  }
};

export type SerializableControlValues = SceneVisualState["controlValues"];

export function clampLatitudeSpringPosition(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export function getLatitudeSpringSeason(position: number): "early-spring" | "late-spring" {
  return clampLatitudeSpringPosition(position) >= 50 ? "late-spring" : "early-spring";
}

export function getLatitudeSpringPosition(controls: SerializableControlValues): number {
  const stored = controls.latitudeSpringPosition;
  if (typeof stored === "number" && Number.isFinite(stored)) {
    return clampLatitudeSpringPosition(stored);
  }
  return controls.latitudeSeason === "late-spring" ? 100 : 0;
}

export function withLatitudeSpringEnds(
  controls: SerializableControlValues,
  previousPosition: number,
  nextPosition: number
): SerializableControlValues {
  const from = clampLatitudeSpringPosition(previousPosition);
  const to = clampLatitudeSpringPosition(nextPosition);
  const lower = Math.min(from, to);
  const upper = Math.max(from, to);
  return {
    ...controls,
    latitudeSpringPosition: to,
    latitudeSeason: getLatitudeSpringSeason(to),
    latitudeCity: to >= 50 ? "beijing" : "nanjing",
    "seen:early-spring": controls["seen:early-spring"] === true || lower <= 0,
    "seen:late-spring": controls["seen:late-spring"] === true || upper >= 100
  };
}

export function hasOperatedLatitudeSpring(controls: SerializableControlValues): boolean {
  const position = getLatitudeSpringPosition(controls);
  const seenEarly = controls["seen:early-spring"] === true || position <= 0;
  const seenLate = controls["seen:late-spring"] === true || position >= 100;
  return seenEarly && seenLate;
}

export function clampLongitudeWindPosition(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export function getLongitudeWindPlace(position: number): "beijing-inland" | "dalian-coast" {
  return clampLongitudeWindPosition(position) >= 50 ? "dalian-coast" : "beijing-inland";
}

export function getLongitudeWindPosition(controls: SerializableControlValues): number {
  const stored = controls.longitudeWindPosition;
  if (typeof stored === "number" && Number.isFinite(stored)) {
    return clampLongitudeWindPosition(stored);
  }
  return controls.longitudePlace === "dalian-coast" ? 100 : 0;
}

export function withLongitudeWindEnds(
  controls: SerializableControlValues,
  previousPosition: number,
  nextPosition: number
): SerializableControlValues {
  const from = clampLongitudeWindPosition(previousPosition);
  const to = clampLongitudeWindPosition(nextPosition);
  const lower = Math.min(from, to);
  const upper = Math.max(from, to);
  return {
    ...controls,
    longitudeWindPosition: to,
    longitudePlace: getLongitudeWindPlace(to),
    "seen:beijing-inland": controls["seen:beijing-inland"] === true || lower <= 0,
    "seen:dalian-coast": controls["seen:dalian-coast"] === true || upper >= 100
  };
}

export function hasOperatedLongitudeWind(controls: SerializableControlValues): boolean {
  const position = getLongitudeWindPosition(controls);
  const seenBeijing = controls["seen:beijing-inland"] === true || position <= 0;
  const seenDalian = controls["seen:dalian-coast"] === true || position >= 100;
  return seenBeijing && seenDalian;
}

export function getSemanticSelection(
  factorId: FactorId,
  controls: SerializableControlValues
): Record<string, string> {
  const selected = Object.fromEntries(factorSemantics[factorId].groups.map((group) => [
    group.id,
    typeof controls[group.id] === "string" ? String(controls[group.id]) : group.options[0].value
  ]));
  if (factorId === "latitude") {
    const position = getLatitudeSpringPosition(controls);
    selected.latitudeSeason = getLatitudeSpringSeason(position);
    selected.latitudeCity = position >= 50 ? "beijing" : "nanjing";
  }
  if (factorId === "longitude") {
    selected.longitudePlace = getLongitudeWindPlace(getLongitudeWindPosition(controls));
  }
  return selected;
}

export function selectSemanticOption(
  factorId: FactorId,
  controls: SerializableControlValues,
  groupId: string,
  value: string
): SerializableControlValues {
  const definition = factorSemantics[factorId];
  const group = definition.groups.find((candidate) => candidate.id === groupId);
  if (!group?.options.some((option) => option.value === value)) return { ...controls };
  const current = getSemanticSelection(factorId, controls);
  const seenCurrent = Object.values(current).reduce<SerializableControlValues>((result, selectedValue) => {
    result[`seen:${selectedValue}`] = true;
    return result;
  }, {});
  return { ...controls, ...seenCurrent, [groupId]: value, [`seen:${value}`]: true };
}

export function hasOperatedSemanticControls(
  factorId: FactorId,
  controls: SerializableControlValues
): boolean {
  if (factorId === "latitude") return hasOperatedLatitudeSpring(controls);
  if (factorId === "longitude") return hasOperatedLongitudeWind(controls);
  const selected = getSemanticSelection(factorId, controls);
  return factorSemantics[factorId].groups.every((group) => group.options.every((option) =>
    selected[group.id] === option.value || controls[`seen:${option.value}`] === true
  ));
}

export function getSemanticProgress(factorId: FactorId, controls: SerializableControlValues): {
  visited: number;
  total: number;
} {
  if (factorId === "latitude") {
    const position = getLatitudeSpringPosition(controls);
    const visited = [
      controls["seen:early-spring"] === true || position <= 0,
      controls["seen:late-spring"] === true || position >= 100
    ].filter(Boolean).length;
    return { visited, total: 2 };
  }
  if (factorId === "longitude") {
    const position = getLongitudeWindPosition(controls);
    const visited = [
      controls["seen:beijing-inland"] === true || position <= 0,
      controls["seen:dalian-coast"] === true || position >= 100
    ].filter(Boolean).length;
    return { visited, total: 2 };
  }
  const selected = getSemanticSelection(factorId, controls);
  const options = factorSemantics[factorId].groups.flatMap((group) => group.options.map((option) => ({ group, option })));
  const visited = options.filter(({ group, option }) => selected[group.id] === option.value || controls[`seen:${option.value}`] === true).length;
  return { visited, total: options.length };
}

export function getSemanticStageValue(factorId: FactorId, controls: SerializableControlValues): number {
  if (factorId === "latitude") return getLatitudeSpringPosition(controls) / 100;
  if (factorId === "longitude") return getLongitudeWindPosition(controls) / 100;
  const selected = getSemanticSelection(factorId, controls);
  const values = factorSemantics[factorId].groups.map((group) =>
    group.options.find((option) => option.value === selected[group.id])?.normalized ?? 0
  );
  return values.reduce((sum, value) => sum + value, 0) / Math.max(values.length, 1);
}

export function describeSemanticObservation(factorId: FactorId, controls: SerializableControlValues): string {
  const selected = getSemanticSelection(factorId, controls);
  if (factorId === "latitude") {
    return selected.latitudeSeason === "late-spring"
      ? "南京已过花期，北京开花"
      : "南京开花，北京尚未开放";
  }
  if (factorId === "longitude") {
    return "北京已开花，大连尚未开放";
  }
  if (factorId === "elevation") {
    const level = factorSemantics.elevation.groups[0].options.find((option) => option.value === selected.elevationLevel)?.shortLabel;
    return selected.elevationSeason === "autumn" ? `${level} · 秋季：越高处落叶通常越早` : `${level} · 春夏：越高处抽青、开花通常越迟`;
  }
  return selected.eraPeriod === "1921-1930" ? "1921-1930年的同一时间" : "1741-1750年春季物候到来平均时间";
}

export const factorMappingOptions = (Object.keys(factorSemantics) as FactorId[]).map((factorId) => ({
  factorId,
  claim: factorSemantics[factorId].claim,
  boundary: factorSemantics[factorId].boundary
}));

export function isFactorMappingCorrect(
  factorId: FactorId,
  claimId: string | undefined,
  boundaryId: string | undefined,
  evidenceViewed: boolean
): boolean {
  return evidenceViewed && claimId === factorId && boundaryId === factorId;
}
